import { Configuration as ReportConfiguration, RiderSheetsApi } from "report-common";
import { Configuration, ScoreboardApi } from "score-common";
import { ageHeaderToMs } from "common-fe/scoreboard/entryTimer.js";

// Public score-sheet self-service API helpers. The report-api calls here go
// through the generated report-common RiderSheetsApi (#1911; regenerate it
// with `yarn workspace report-common generate-api-client`, see
// packages/report-common/README.md) and carry no auth: every route is public.
// Base URL comes from VITE_REPORT_API_BASE_URL (report-api, local port 3003).
// The client is built once at module load.
const BASE_URL = import.meta.env.VITE_REPORT_API_BASE_URL;
const riderSheetsApi = new RiderSheetsApi(new ReportConfiguration({ basePath: BASE_URL }));

// #1637 — the public scoreboard talks to score-api, not report-api. Net-new
// base URL for this app (design docs/score/scoreboard-design.md §4.1).
// #1908: the three score-api reads below go through the generated
// score-common ScoreboardApi (no token: every route is allowNoAuth) instead of
// a hand-rolled fetch. The client is built once at module load.
const SCORE_BASE_URL = import.meta.env.VITE_SCORE_API_BASE_URL;
const scoreboardApi = new ScoreboardApi(new Configuration({ basePath: SCORE_BASE_URL }));

// Card #3302 -- the PHONE scoreboard's base URL. In a deployed build this is
// the page's OWN origin: score-fe's CloudFront distribution routes
// /public/scoreboard/phone/* to score-api behind a 2s edge cache (apps/score-fe/
// sam.d/template.yml), so the phone is same-origin (no CORS) and a crowd is
// answered by the edge, not the Lambda. The arena board never uses this: it
// keeps calling score-api directly on SCORE_BASE_URL, uncached. In dev/test
// there is no CloudFront, so the phone path talks to score-api like any other
// read; VITE_SCORE_PHONE_BASE_URL overrides either way.
const PHONE_BASE_URL =
  import.meta.env.VITE_SCORE_PHONE_BASE_URL ||
  (import.meta.env.PROD && typeof window !== "undefined" ? window.location.origin : SCORE_BASE_URL);
const phoneScoreboardApi = new ScoreboardApi(new Configuration({ basePath: PHONE_BASE_URL }));

// `cache: "no-store"` for every score-api read below. The generated client
// goes through axios, whose default XHR adapter has no cache control, and a
// `Cache-Control` request header would add a CORS preflight the public API
// does not allow-list. axios's fetch adapter forwards `fetchOptions` straight
// to fetch(), which keeps the exact no-store semantics the hand-rolled
// fetches had (the 5s poll, the one-shot QR scan, a rider's reload/rescan).
const NO_STORE = { adapter: "fetch", fetchOptions: { cache: "no-store" } };

// Runs one generated-client call and returns the parsed JSend body, or throws.
// Reproduces the old fetch contract: a non-2xx response (axios rejects it with
// `err.response`), a non-JSON body, or a body whose `status` is not "success"
// all throw an Error whose message is the server's own `data.message`, else
// `${what} request failed (${status})`. `withDetail` also carries `.code`
// (the JSend `code`) and `.status` on the Error, for callers that branch on
// them. A transport failure with no response (network down) is rethrown as-is,
// as axios's own error (its message is "Network Error", not a fetch TypeError).
async function readJsend(call, what, { withDetail = false, stampEntryTimer = false } = {}) {
  let status;
  let body = null;
  let ageHeader;
  try {
    const res = await call();
    status = res.status;
    body = res.data;
    ageHeader = res.headers?.age;
  } catch (err) {
    if (!err?.response) throw err;
    status = err.response.status;
    body = err.response.data;
  }
  // Card #3614 -- the public board's bell-to-entry timer is counted down locally
  // from the moment THIS response landed. Stamp it with that monotonic moment and
  // the response's Age header (the phone path's edge cache holds a copy up to 2 s;
  // the uncached venue path has no Age, so 0), so the board subtracts both.
  if (stampEntryTimer && body?.status === "success" && body.data?.entryTimer && typeof body.data.entryTimer === "object") {
    return {
      ...body.data,
      entryTimer: {
        ...body.data.entryTimer,
        receivedAtMs: performance.now(),
        ageMs: ageHeaderToMs(ageHeader),
      },
    };
  }
  if (!body || typeof body !== "object" || body.status !== "success") {
    const message = body?.data?.message || `${what} request failed (${status})`;
    const error = new Error(message);
    if (withDetail) {
      error.code = body?.code || null;
      error.status = status;
    }
    throw error;
  }
  return body.data;
}

// The report-api reads below hand their callers `{ status, body }` and let the
// caller branch on the status (202 found / 200 not-found / 400 validation /
// 404 / 410), so axios must not reject on a non-2xx: `validateStatus` accepts
// every status. A transport failure with no response still rejects. A body
// that is not a JSON object (empty, HTML from a gateway) reads as `{}`, as
// the old `res.json()` try/catch did.
const ANY_STATUS = { validateStatus: () => true };
function statusAndBody(res) {
  const data = res.data;
  return { status: res.status, body: data && typeof data === "object" ? data : {} };
}

// GET config (#1957 — data-driven show list, replaces the pinned single-show
// shape): { configured, scoreSiteUrl, shows: [{id, name, startDate, endDate,
// location, isCurrent}], defaultShowIds }. `shows` is already sorted
// reverse-chronological by report-api; `defaultShowIds` names whichever
// show(s) are currently running, or the most recent one(s) when none is.
export async function fetchConfig() {
  const res = await riderSheetsApi.getPublicScoreSheetsConfig(ANY_STATUS);
  if (res.status < 200 || res.status >= 300) {
    throw new Error(`config request failed (${res.status})`);
  }
  // A 2xx whose body is not a JSON object (gateway HTML) made the old
  // `res.json()` throw; axios hands the raw string back, so keep that contract.
  if (!res.data || typeof res.data !== "object") {
    throw new Error(`config request failed (${res.status}): response was not JSON`);
  }
  return res.data;
}

// POST request-by-email+CNO+showId. Returns { status, body } so callers can
// branch on the HTTP status (202 found / 200 not-found / 400 validation).
// #1957 — showId is now required: the visitor's dropdown pick (or the
// config's own default), never a server-pinned show.
export async function requestScoreSheet({ email, cno, showId }) {
  return statusAndBody(
    await riderSheetsApi.requestPublicScoreSheets({ email, cno, showId }, ANY_STATUS)
  );
}

// GET a generated bundle by token. Returns { status, body }; 404 => unknown token.
export async function fetchScoreSheet(token) {
  return statusAndBody(await riderSheetsApi.getPublicScoreSheets(token, ANY_STATUS));
}

// Card #971 — click-time presign pass-through. SheetsPage's View/Download
// buttons never carry a raw S3 URL; instead they link to this app's own
// /open/:requestId/:rideId/:variant interstitial, which calls this and
// redirects to whatever `url` it gets back. Returns { status, body } so the
// interstitial can branch on: 200 { url } ready, 202 { status: "generating" }
// still rendering, 404 unknown / withheld, 410 { status: "unavailable" }
// gone-and-past-the-regen-window.
// `cache: "no-store"` is load-bearing, not cosmetic: this same URL is polled
// repeatedly (the "generating" state) and revisited across a rider's clicks,
// and several of the possible responses (404, 410) are heuristically
// cacheable per RFC 9111 with no explicit Cache-Control from report-api. A
// browser-cached 410/404 would silently keep serving a stale verdict even
// after the artifact exists — defeating the whole point of minting fresh at
// click time. (NO_STORE: see its comment above, axios's fetch adapter.)
export async function fetchOpenUrl({ requestId, rideId, variant }) {
  return statusAndBody(
    await riderSheetsApi.getPublicScoreSheetUrl(requestId, rideId, variant, {
      ...NO_STORE,
      ...ANY_STATUS,
    })
  );
}

// #1637 — GET the composed public scoreboard envelope (design §3.1, built by
// card #1636's apps/score-api/src/handlers/scoreboard-public.js): view, mode,
// theme, sponsor, payload, serverTime, in one poll-friendly read. `slug`
// omitted resolves the show's default view. `cache: "no-store"` — this is
// polled every 5s and must never serve a browser-cached stale envelope.
//
// Throws on a non-2xx response or a malformed body so the caller's poll loop
// (useScoreboardPoll) can treat it as a failed poll and keep the last good
// payload on screen, per design §4.2 — never resolves to a half-built object.
//
// Card #2493: `previewModeId` asks for the payload of a mode that is NOT live,
// composed server-side exactly as if it were (the remote's local preview). It is
// a read; nothing on any live screen changes.
export async function fetchScoreboard({ showId, year, slug, previewModeId = null }) {
  const segments = [showId, year, slug].filter((s) => s != null && s !== "");
  const preview = previewModeId || undefined;
  // The route's second segment is a year OR (short form) a slug, so two
  // segments is the default-view route and three is the explicit-slug route.
  // Fewer cannot address any route: mirror the 404 the server gave for it.
  let call;
  if (segments.length === 3) {
    call = () => scoreboardApi.getPublicScoreboard(segments[0], segments[1], segments[2], preview, NO_STORE);
  } else if (segments.length === 2) {
    call = () => scoreboardApi.getPublicScoreboardDefaultView(segments[0], segments[1], preview, NO_STORE);
  } else {
    throw new Error("scoreboard request failed (404)");
  }
  return readJsend(call, "scoreboard", { stampEntryTimer: true });
}

// Card #3302 -- GET the PHONE variant of the composed envelope: same shape as
// fetchScoreboard plus `pollAfterMs`, from the CloudFront-cached phone route.
// No `NO_STORE` here on purpose: fetch's no-store mode adds `Cache-Control:
// no-cache` to the request, and the point of this path is to be answered by
// the edge cache. Freshness is the server's: the reply is `max-age=0` for the
// browser and `s-maxage=2` for the edge. No previewModeId either: a preview is
// never edge-cached. Same throw-on-non-2xx / malformed-body contract, so a bad
// poll keeps the last good payload on screen and the board never shows an
// error (a CloudFront-substituted index.html for a missing show reads as a
// malformed body).
//
// A request timeout is mandatory here: axios has none by default, and the poll
// chain (useScoreboardPoll) only schedules the next poll once the current one
// settles, so one request hung on a flaky phone connection would stop that
// phone's board for good. On timeout the call rejects, the poll keeps the last
// good payload, and the chain carries on.
export const PHONE_REQUEST_TIMEOUT_MS = 10000;
const PHONE_REQUEST = { timeout: PHONE_REQUEST_TIMEOUT_MS };

export async function fetchScoreboardPhone({ showId, year, slug }) {
  const segments = [showId, year, slug].filter((s) => s != null && s !== "");
  let call;
  if (segments.length === 3) {
    call = () => phoneScoreboardApi.getPublicScoreboardPhone(segments[0], segments[1], segments[2], PHONE_REQUEST);
  } else if (segments.length === 2) {
    call = () => phoneScoreboardApi.getPublicScoreboardPhoneDefaultView(segments[0], segments[1], PHONE_REQUEST);
  } else {
    throw new Error("scoreboard request failed (404)");
  }
  return readJsend(call, "scoreboard", { stampEntryTimer: true });
}

// Card #3307 -- GET the spectators' Live page read: every ring of one show,
// addressed by the show's short public Live code, from the SAME cached phone
// prefix as the phone board (/public/scoreboard/phone/live/:code). Same
// no-NO_STORE and same 10s timeout reasoning as fetchScoreboardPhone above: the
// edge cache answers the crowd, and a hung request must not stop the poll chain.
// An unknown code 404s, which reads as a thrown Error; the page treats that as
// an empty page and keeps polling (the edge caches a 404 for 10s, #3302).
export async function fetchLivePhone({ code }) {
  if (!code) throw new Error("live request failed (404)");
  // Upper-cased so every spelling of a code (a typed "abc234", a scanned one)
  // is ONE edge-cache entry; the server is case-insensitive either way.
  const upper = String(code).toUpperCase();
  return readJsend(() => phoneScoreboardApi.getPublicLivePhone(upper, PHONE_REQUEST), "live");
}

// Card #3560 (docs/adr/0003) -- the board's CHANGE CHECK: GET the show's change
// signal (counters only, ONE DynamoDB read server side) instead of pulling the
// whole composed payload every beat. Two public reads with the same body:
//   fetchPublicChanges       the venue board. Uncached (NO_STORE), like the
//                            board's own arena read.
//   fetchPublicPhoneChanges  the phone board and the Live page. CloudFront
//                            caches it 2s, so the cost is about one origin read
//                            per show per 2s whatever the crowd (no NO_STORE,
//                            same reason as fetchScoreboardPhone), and the same
//                            10s timeout so one hung request cannot stop a
//                            phone's check chain.
// Both throw on a non-2xx or malformed body; the caller treats a throw as "no
// answer this beat" and keeps what is on screen (the board never shows an error).
export async function fetchPublicChanges({ showId }) {
  if (!showId) throw new Error("changes request failed (404)");
  return readJsend(() => scoreboardApi.getPublicChanges(showId, NO_STORE), "changes");
}

export async function fetchPublicPhoneChanges({ showId }) {
  if (!showId) throw new Error("changes request failed (404)");
  return readJsend(() => phoneScoreboardApi.getPublicPhoneChanges(showId, PHONE_REQUEST), "changes");
}

// #1641 — GET the public class-results read (design §3.1/§6.3, built by
// apps/score-api/src/handlers/scoreboard-public.js's getPublicClassResults):
// the QR-code target, `/public/scoreboard/:showId/class/:classId/results`.
// Confirmed/final rows only, same exposure rules as the board. `year` is
// NOT part of this path (unlike fetchScoreboard's) — the endpoint does no
// year check at all, so ClassResultsPage's `/results/:showId/:year/:classNumber`
// (#1854 — renamed from :classId) route param is FE-side/cosmetic only and
// never sent here.
//
// Same "throw on non-2xx or malformed body" contract as fetchScoreboard, but
// the thrown Error also carries `.code` (one of show_not_found/
// view_not_found/class_not_found on a 404) and `.status` so the page can
// render every 404 the same generic "not available" way (per the card: a
// view_not_found on a real show/class is an exposure gate, not "does not
// exist", so the FE must not claim non-existence from the code alone).
// `cache: "no-store"` — this is a one-shot QR-scan read, not the 5s poll,
// but a rider could still reload/rescan and must never get a browser-cached
// stale verdict.
// #1854 — GET the public showId+classNumber -> classId resolver (built by
// apps/score-api/src/handlers/results.js's resolveClassByNumber):
// `/public/scoreboard/:showId/class-number/:classNumber`. The board's QR now
// encodes a short, human-readable class NUMBER rather than the internal
// fvd: classId, so ClassResultsPage calls this FIRST and only then calls
// fetchClassResults below with the resolved classId — the classId-keyed
// endpoint itself is unchanged. Same "throw on non-2xx or malformed body,
// carry .code/.status" contract as fetchClassResults, so both can share one
// error-rendering path in the page (every 404 — show_not_found,
// view_not_found, class_not_found — reads the same generic "not available"
// way; a non-public show must not be distinguishable from a nonexistent
// one). `cache: "no-store"` for the same one-shot-QR-scan reasoning as
// fetchClassResults.
export async function resolveClassId({ showId, classNumber }) {
  return readJsend(
    () => scoreboardApi.resolveClassByNumber(showId, String(classNumber), NO_STORE),
    "class resolve",
    { withDetail: true }
  );
}

export async function fetchClassResults({ showId, classId }) {
  return readJsend(
    () => scoreboardApi.getPublicClassResults(showId, classId, NO_STORE),
    "class results",
    { withDetail: true }
  );
}
